import React, { useState, useRef, useEffect } from 'react';
import { Users, AlertCircle } from 'lucide-react';

/**
 * RigaAttribuzioneBtn: piccolo bottone "👥" da affiancare a ogni riga di preventivo
 * che apre un popover con:
 *   - Dropdown "Pagatore" (visibile solo se clienti.length >= 2)
 *   - Checkbox "Fuori contabilità EdilCima"
 *
 * Props:
 *   riga: {pagatore_id, fuori_contabilita}
 *   clienti: [{id, label, ...}]  → lista pagatori disponibili
 *   onChange: (patch: {pagatore_id?, fuori_contabilita?}) => void
 *   testid?: string
 */
export default function RigaAttribuzioneBtn({ riga, clienti = [], onChange, testid = 'riga-attrib' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const multiCliente = clienti.length >= 2;
  const pagatore = clienti.find(c => c.id === riga?.pagatore_id) || clienti[0];
  const fuori = !!riga?.fuori_contabilita;

  // Se non ci sono opzioni e non è fuori contabilità, nascondi del tutto il bottone
  if (!multiCliente && !fuori && clienti.length < 2) {
    // Mostra comunque solo l'icona "fuori contabilità" toggleabile (utile anche con 1 cliente)
  }

  // Colore badge: arancione se attivo (non default), grigio altrimenti
  const isCustom = (multiCliente && pagatore && riga?.pagatore_id && clienti[0]?.id !== riga.pagatore_id) || fuori;
  const btnColor = fuori ? 'bg-red-100 text-red-700 hover:bg-red-200'
    : isCustom ? 'bg-primary text-primary hover:bg-primary'
    : 'bg-slate-100 text-slate-500 hover:bg-slate-200';

  // Label visibile sul bottone
  let label = 'Attribuzione';
  if (fuori) label = '⚠ Fuori cont.';
  else if (multiCliente && pagatore) label = pagatore.label || 'Pagatore';

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)}
        className={`text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1 ${btnColor} whitespace-nowrap`}
        title="Attribuzione pagatore / Fuori contabilità"
        data-testid={`${testid}-btn`}>
        {fuori ? <AlertCircle size={11} /> : <Users size={11} />}
        <span className="max-w-[80px] truncate">{label}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-white border border-slate-200 rounded-lg shadow-xl p-3" data-testid={`${testid}-popover`}>
          {multiCliente && (
            <div className="mb-3">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-1">Pagatore</label>
              <select value={riga?.pagatore_id || clienti[0]?.id || ''}
                onChange={e => onChange({ pagatore_id: e.target.value })}
                disabled={fuori}
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                data-testid={`${testid}-pagatore-select`}>
                {clienti.map(c => (
                  <option key={c.id} value={c.id}>{c.label}{c.id === clienti[0].id ? ' (principale)' : ''}</option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-red-50 border border-red-100">
            <input type="checkbox" checked={fuori}
              onChange={e => onChange({ fuori_contabilita: e.target.checked })}
              className="mt-0.5"
              data-testid={`${testid}-fuori-cont-checkbox`} />
            <div className="flex-1">
              <p className="text-xs font-bold text-red-700">Fuori contabilità EdilCima</p>
              <p className="text-[10px] text-slate-500 leading-tight mt-0.5">
                La voce è eseguita ma <b>pagata dal cliente direttamente a terzi</b>. Non verrà fatturata, ma comparirà nel SAL.
              </p>
            </div>
          </label>
          {!multiCliente && (
            <p className="text-[10px] text-slate-400 italic mt-2 px-1">
              Per attribuire la riga a un pagatore specifico, aggiungi prima più clienti al cantiere/pratica.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
